/**
 * 898facce.js / tmp_gate.js 反爬逻辑 1:1 可读实现
 * 高分不拦截，结果写入 _m / _r（与仿站一致）
 */

import { getSensitiveWord } from "./sensitiveWords";

const platformLower = String(navigator.platform || "")
  .toLowerCase()
  .trim();
/** 898facce：navigator.platform 以 win / mac 开头（非 UA 里的 chrome） */
export const isDesktopPlatform =
  platformLower.indexOf("win") === 0 || platformLower.indexOf("mac") === 0;
const isMobileUa = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(
  navigator.userAgent || ""
);
const isSmallMobile = isMobileUa && window.innerWidth <= 0x384;
/** _0x5cd2ab：桌面 Win/Mac 或非小屏移动端才启用反调试 */
export const isAntiDebugTarget =
  isDesktopPlatform || (isMobileUa && !isSmallMobile);

/** 898facce.js _0x6ab393 顺序 */
const AUTO_GLOBAL_KEYS = [
  "__webdriver_evaluate",
  "__selenium_evaluate",
  "__webdriver_script_function",
  "__webdriver_script_fn",
  "__fxdriver_evaluate",
  "__driver_unwrapped",
  "__webdriver_script_func",
  "__selenium_unwrapped",
  "__fxdriver_unwrapped",
  "__driver_evaluate",
  "__selenium_unwrapped",
  "__fxdriver_unwrapped",
  "domAutomation",
  "domAutomationController",
];

const MODULE_WEIGHTS = {
  m2: 0.15,
  m3: 0.25,
  m4: 0.2,
  m5: 0.2,
  m6: 0.15,
  m7: 0.05,
};

const MODULE_NUMERIC_IDS = {
  m2: 2,
  m3: 3,
  m4: 4,
  m5: 5,
  m6: 6,
  m7: 7,
};

const behaviorState = {
  mouseMoves: 0,
  mouseClicks: 0,
  scrollEvents: 0,
  keydowns: 0,
  focusChanges: 0,
  hasTouchMove: false,
  pageLoadTime: Date.now(),
};

const batchState = {
  selectionCount: 0,
  copyCount: 0,
  domQueryCount: 0,
};

let behaviorBound = false;
let batchBound = false;

/** 仿站：保留 URL 上除 _m/_r/_t 外的 query */
export function getPreservedSearchQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const skip = { _m: 1, _r: 1, _t: 1, f: 1, F: 1 };
    const parts = [];
    params.forEach((value, key) => {
      if (!skip[key]) {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        );
      }
    });
    return parts.join("&");
  } catch {
    return "";
  }
}

export function collectEnvFingerprint() {
  const info = {};
  try {
    info.ua = navigator.userAgent || "";
    info.platform = navigator.platform || "";
    info.lang = navigator.language || "";
    info.cores = navigator.hardwareConcurrency || 0;
    info.mem = navigator.deviceMemory || 0;
    info.cookies = navigator.cookieEnabled;
    info.online = navigator.onLine;
    info.dpr = window.devicePixelRatio || 1;
    info.sw = window.screen?.width || 0;
    info.sh = window.screen?.height || 0;
    info.iw = window.innerWidth || 0;
    info.ih = window.innerHeight || 0;
    info.ow = window.outerWidth || 0;
    info.oh = window.outerHeight || 0;
    try {
      localStorage.setItem("_t", "1");
      localStorage.removeItem("_t");
      info.ls = true;
    } catch {
      info.ls = false;
    }
    try {
      sessionStorage.setItem("_t", "1");
      sessionStorage.removeItem("_t");
      info.ss = true;
    } catch {
      info.ss = false;
    }
    info.hasCanvas = (() => {
      const c = document.createElement("canvas");
      return !!(c.getContext && c.getContext("2d"));
    })();
    info.hasWebGL = (() => {
      try {
        const c = document.createElement("canvas");
        return !!(
          c.getContext("webgl") || c.getContext("experimental-webgl")
        );
      } catch {
        return false;
      }
    })();
    info.hasWorker = typeof Worker !== "undefined";
    info.pdfViewer = "pdfViewerEnabled" in navigator;
    info.readyState = document.readyState;
    info.title = document.title || "";
    info.ts = Date.now();
  } catch {
    /* ignore */
  }
  return { score: 0, hit: false, info };
}

export function scoreEnvMismatch(info) {
  const reasons = [];
  let score = 0;
  try {
    const ua = info.ua || "";
    const platform = info.platform || "";
    const mobileUa = /Android|iPhone|iPad|iPod/i.test(ua);
    const armPlat = /arm|iphone|ipad|android/i.test(platform);
    if (mobileUa !== armPlat) {
      score += 10;
      reasons.push("ua-platform-mismatch");
    }
    const mobileLike = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    const hasTouch =
      navigator.maxTouchPoints > 0 || "ontouchstart" in window;
    if (mobileLike && !hasTouch) {
      score += 10;
      reasons.push("mobile-no-touch");
    }
    if (info.sw < 0x64 || info.sh < 0x64) {
      score += 10;
      reasons.push("screen-too-small");
    }
    if (info.dpr > 2 && info.sw < 0x190) {
      score += 5;
      reasons.push("dpr-screen-mismatch");
    }
    if (!info.hasCanvas) {
      score += 5;
      reasons.push("no-canvas");
    }
    if (!info.lang) {
      score += 5;
      reasons.push("no-language");
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x28), hit: score > 0, reasons };
}

export function scoreAutomation(info) {
  const reasons = [];
  let score = 0;
  try {
    const ua = info.ua || "";
    if (/HeadlessChrome|PhantomJS|SlimerJS|Nightmare/i.test(ua)) {
      score += 0x1e;
      reasons.push("headless-ua");
    }
    if (navigator.webdriver === true) {
      score += 0x1e;
      reasons.push("webdriver");
    }
    for (const key of AUTO_GLOBAL_KEYS) {
      if (key in window) {
        score += 0x14;
        reasons.push(`auto-global:${key}`);
        break;
      }
    }
    if (
      navigator.plugins &&
      navigator.plugins.length === 0 &&
      isDesktopPlatform
    ) {
      score += 10;
      reasons.push("no-plugins");
    }
    const isChromeNotChromium =
      /Chrome/i.test(ua) && !/Chromium/i.test(ua);
    const hasChromeObj = !!(
      window.chrome &&
      (window.chrome.runtime ||
        window.chrome.webstore ||
        window.chrome.app)
    );
    if (isChromeNotChromium && !hasChromeObj) {
      score += 0xf;
      reasons.push("chrome-object-missing");
    }
    try {
      const ev = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        clientX: 0x64,
        clientY: 0x64,
      });
      if (
        typeof ev.isTrusted === "undefined" ||
        ev.isTrusted === undefined
      ) {
        score += 10;
        reasons.push("istrusted-undefined");
      }
    } catch {
      score += 10;
      reasons.push("mouseevent-fail");
    }
    if (navigator.permissions) {
      try {
        navigator.permissions
          .query({ name: "notifications" })
          .then(() => {})
          .catch(() => {});
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x3c), hit: score > 0, reasons };
}

export function scoreBehaviorProbe(track) {
  const reasons = [];
  let score = 0;
  try {
    const elapsed = Date.now() - behaviorState.pageLoadTime;
    if (elapsed < 0x320) {
      score += 0x14;
      reasons.push("page-too-fast");
    }
    if (
      isDesktopPlatform &&
      !isMobileUa &&
      behaviorState.mouseMoves < 3
    ) {
      score += 0xf;
      reasons.push("no-mouse-move");
    }
    if (
      isDesktopPlatform &&
      !isMobileUa &&
      behaviorState.mouseClicks < 1
    ) {
      score += 0xf;
      reasons.push("no-mouse-click");
    }
    if (track && track.length < 3) {
      score += 10;
      reasons.push("track-too-short");
    }
    if (track && track.length >= 3) {
      const speeds = [];
      for (let i = 1; i < track.length; i++) {
        const dt = track[i].t - track[i - 1].t;
        const dx = track[i].x - track[i - 1].x;
        if (dt > 0) speeds.push(dx / dt);
      }
      if (speeds.length >= 2) {
        const avg =
          speeds.reduce((a, b) => a + b, 0) / speeds.length;
        const variance =
          speeds.reduce((a, b) => a + Math.pow(b - avg, 2), 0) /
          speeds.length;
        if (variance < 0.01) {
          score += 0xf;
          reasons.push("track-too-linear");
        }
      }
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x28), hit: score > 0, reasons };
}

export function scoreDebugTools() {
  const reasons = [];
  let score = 0;
  try {
    if (isAntiDebugTarget) {
      if (window.innerWidth < 0x384) {
        score += 0x19;
        reasons.push("inner-width-small");
      }
      if (Math.abs(window.outerWidth - window.innerWidth) > 0xc8) {
        score += 0x14;
        reasons.push("devtools-dock");
      }
    }
    if (
      window.Firebug &&
      window.Firebug.chrome &&
      window.Firebug.chrome.isInitialized
    ) {
      score += 0x19;
      reasons.push("firebug-detected");
    }
    if (typeof console !== "undefined") {
      const keyCount = Object.keys(console).length;
      if (keyCount > 0x14) {
        score += 10;
        reasons.push("console-keys-many");
      }
    }
    try {
      throw new Error("_");
    } catch (e) {
      if (e?.stack && /debugger/i.test(e.stack)) {
        score += 0xf;
        reasons.push("debugger-in-stack");
      }
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x32), hit: score > 0, reasons };
}

export function scoreHookedApis() {
  const reasons = [];
  let score = 0;
  try {
    const fnToString = Function.prototype.toString;
    const selfStr = fnToString.call(fnToString);
    if (!/\[native code\]/.test(selfStr)) {
      score += 0x14;
      reasons.push("toString-hooked");
    }
    const checks = [
      ["createElement", document.createElement],
      ["querySelector", document.querySelector],
      ["fetch", window.fetch],
      [
        "xhrOpen",
        window.XMLHttpRequest &&
          XMLHttpRequest.prototype.open,
      ],
    ];
    for (const [name, fn] of checks) {
      if (!fn) continue;
      try {
        const s = Function.prototype.toString.call(fn);
        if (!/\[native code\]/.test(s)) {
          score += 10;
          reasons.push(`fn-hooked:${name}`);
        }
      } catch {
        score += 10;
        reasons.push(`fn-proxy:${name}`);
      }
    }
    try {
      Function.prototype.toString.call(navigator.userAgent);
    } catch {
      /* ignore */
    }
    try {
      const desc =
        Object.getOwnPropertyDescriptor(
          Document.prototype,
          "createElement"
        ) ||
        Object.getOwnPropertyDescriptor(
          HTMLDocument.prototype,
          "createElement"
        );
      if (desc?.get) {
        const s = Function.prototype.toString.call(desc.get);
        if (!/\[native code\]/.test(s)) {
          score += 0xf;
          reasons.push("createElement-getter-hooked");
        }
      }
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x32), hit: score > 0, reasons };
}

export function scoreBatchOps() {
  const reasons = [];
  let score = 0;
  try {
    if (batchState.selectionCount > 10) {
      score += 0xf;
      reasons.push("mass-selection");
    }
    if (batchState.copyCount > 3) {
      score += 10;
      reasons.push("mass-copy");
    }
    if (batchState.domQueryCount > 0x32) {
      score += 0xf;
      reasons.push("mass-dom-query");
    }
    const childLen = document.body
      ? document.body.children.length
      : 0;
    if (childLen === 0) {
      score += 10;
      reasons.push("empty-dom");
    }
  } catch {
    /* ignore */
  }
  return { score: Math.min(score, 0x28), hit: score > 0, reasons };
}

export function aggregateAntiBot(modules) {
  let total = 0;
  const hitModules = [];
  for (const [key, weight] of Object.entries(MODULE_WEIGHTS)) {
    const mod = modules[key];
    if (!mod) continue;
    total += (mod.score || 0) * weight;
    if (mod.hit) hitModules.push(MODULE_NUMERIC_IDS[key]);
  }
  const totalScore = Math.min(Math.round(total), 0x64);
  let level = "low";
  if (totalScore >= 0x3c) level = "high";
  else if (totalScore >= 0x1e) level = "medium";
  return { totalScore, hitModules, level, modules };
}

/** 仿站 _0x489cf2：跑全套检测，不因高分拦截 */
export function runAntiBotChecks(track) {
  const fp = collectEnvFingerprint();
  const modules = {
    m2: scoreEnvMismatch(fp.info),
    m3: scoreAutomation(fp.info),
    m4: scoreBehaviorProbe(track),
    m5: scoreDebugTools(),
    m6: scoreHookedApis(),
    m7: scoreBatchOps(),
  };
  const result = aggregateAntiBot(modules);
  return { ...result, blocked: false };
}

export function validateSliderGesture(track, maxOffset, finalOffset, durationMs) {
  const reached = maxOffset > 0 && finalOffset >= maxOffset * 0.9;
  const ok =
    reached && track.length >= 2 && durationMs > 0x78;
  if (!reached) {
    return { ok: false, message: getSensitiveWord("请滑动到最右侧") };
  }
  if (ok) {
    return { ok: true, message: "" };
  }
  if (durationMs <= 0x78) {
    return {
      ok: false,
      message: getSensitiveWord("滑动速度过快，请重试"),
    };
  }
  return { ok: false, message: getSensitiveWord("请匀速滑动验证") };
}

export function isDevToolsOpen() {
  if (!isAntiDebugTarget) return false;
  if (window.innerWidth < 0x384) return true;
  if (Math.abs(window.outerWidth - window.innerWidth) > 0xc8) {
    return true;
  }
  if (
    window.Firebug &&
    window.Firebug.chrome &&
    window.Firebug.chrome.isInitialized
  ) {
    return true;
  }
  return false;
}

/** 仿站 _0x4c7990 */
export function forceCloseOnDevTools() {
  try {
    window.close();
  } catch {
    /* ignore */
  }
  window.setTimeout(() => {
    window.location.replace("about:blank");
  }, 0xc8);
}

function bindBehaviorProbe() {
  if (behaviorBound) return;
  behaviorBound = true;
  const onMove = () => {
    behaviorState.mouseMoves++;
  };
  const onClick = () => {
    behaviorState.mouseClicks++;
  };
  const onScroll = () => {
    behaviorState.scrollEvents++;
  };
  const onKey = () => {
    behaviorState.keydowns++;
  };
  const onTouchMove = () => {
    behaviorState.hasTouchMove = true;
  };
  const onFocus = () => {
    behaviorState.focusChanges++;
  };
  document.addEventListener("mousemove", onMove, { passive: true });
  document.addEventListener("click", onClick, { passive: true });
  document.addEventListener("touchstart", onClick, { passive: true });
  document.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("keydown", onKey, { passive: true });
  document.addEventListener("touchmove", onTouchMove, { passive: true });
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onFocus);
}

function bindBatchProbe() {
  if (batchBound) return;
  batchBound = true;
  document.addEventListener(
    "selectionchange",
    () => {
      batchState.selectionCount++;
    },
    { passive: true }
  );
  document.addEventListener(
    "copy",
    () => {
      batchState.copyCount++;
    },
    { passive: true }
  );
  try {
    const orig = document.querySelectorAll.bind(document);
    document.querySelectorAll = function querySelectorAll(...args) {
      batchState.domQueryCount++;
      return orig(...args);
    };
  } catch {
    /* ignore */
  }
}

function bindKeyBlock() {
  if (!isAntiDebugTarget) return () => {};
  const onCtx = (e) => {
    e.preventDefault();
    return false;
  };
  const blockKeys = (e) => {
    if (e.keyCode === 0x7b) {
      e.preventDefault();
      return false;
    }
    if (e.ctrlKey && e.shiftKey && e.keyCode === 0x49) {
      e.preventDefault();
      return false;
    }
    if (e.ctrlKey && e.shiftKey && e.keyCode === 0x4a) {
      e.preventDefault();
      return false;
    }
    if (e.ctrlKey && e.shiftKey && e.keyCode === 0x43) {
      e.preventDefault();
      return false;
    }
    if (e.ctrlKey && e.keyCode === 0x55) {
      e.preventDefault();
      return false;
    }
    if (e.ctrlKey && e.keyCode === 0x53) {
      e.preventDefault();
      return false;
    }
  };
  document.addEventListener("contextmenu", onCtx);
  document.addEventListener("keydown", blockKeys);
  return () => {
    document.removeEventListener("contextmenu", onCtx);
    document.removeEventListener("keydown", blockKeys);
  };
}

/** 仿站 _0x1cb4cd + _0x1fda56，页面加载时调用 */
export function initGateAntiBot() {
  if (isDevToolsOpen()) {
    forceCloseOnDevTools();
    return { dispose: () => {} };
  }
  bindBehaviorProbe();
  bindBatchProbe();
  const unbindKeys = bindKeyBlock();
  let intervalId = null;
  if (isAntiDebugTarget) {
    const check = () => {
      if (isDevToolsOpen()) forceCloseOnDevTools();
    };
    intervalId = window.setInterval(check, 0x3e8);
    window.addEventListener("resize", check);
    return {
      dispose() {
        unbindKeys();
        if (intervalId) window.clearInterval(intervalId);
        window.removeEventListener("resize", check);
      },
    };
  }
  return {
    dispose() {
      unbindKeys();
    },
  };
}

/** @deprecated 使用 initGateAntiBot */
export function createBehaviorProbe() {
  bindBehaviorProbe();
  return {
    state: behaviorState,
    dispose() {},
  };
}

/** @deprecated 使用 initGateAntiBot */
export function bindDesktopAntiDebug() {
  return initGateAntiBot().dispose;
}
